import { MenuTextMatch } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { $isElementNode, $isTextNode, LexicalNode, TextNode } from "lexical";
import { $matchVariable } from "../../hooks/useVariables";
import { VariableNode } from "../../nodes/VariableNode";

/** Strips wrapping {{ and }} from a variable key. */
function getVariableKeyWithoutWrappers(variableKey: string): string {
  return variableKey.replace(/^{{|}}$/g, "");
}

/** Creates a segmented, directionless VariableNode from a key. */
export function createVariableNode(
  variableKey: string,
  isValid: boolean = true
): VariableNode {
  const cleanedKey = getVariableKeyWithoutWrappers(variableKey);
  const variableNode = new VariableNode(cleanedKey, isValid);
  variableNode.setMode("segmented").toggleDirectionless();
  return variableNode;
}

/** Type guard for VariableNode. */
export function isVariableNode(
  node: LexicalNode | null | undefined
): node is VariableNode {
  return node instanceof VariableNode;
}

/** Matches mustache-style variable syntax in text and returns a MenuTextMatch. */
export function matchMustacheVariableInText(
  text: string,
  exact: boolean = false,
  minMatchLength: number = 0
): MenuTextMatch | null {
  const match = $matchVariable(text, exact);

  if (match !== null) {
    const matchingString = match[2] ?? "";
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index,
        matchingString,
        replaceableString: match[1] ?? "",
      };
    }
  }
  return null;
}

/** Splits a TextNode so only the matched portion remains in the returned node. */
export function splitNodeContainingQuery(
  anchorNode: TextNode,
  match: MenuTextMatch
): TextNode | null {
  if (!anchorNode.isSimpleText()) {
    return null;
  }

  const startOffset = match.leadOffset;
  const matchOffset = startOffset + match.replaceableString.length;

  let newNode;
  if (startOffset === 0 && matchOffset === anchorNode.getTextContent().length) {
    newNode = anchorNode;
  } else if (startOffset === 0) {
    [newNode] = anchorNode.splitText(startOffset, matchOffset);
  } else {
    [, newNode] = anchorNode.splitText(startOffset, matchOffset);
  }
  return newNode ?? null;
}

/** Replaces a TextNode with a VariableNode if it contains a complete variable match. */
export function maybeTransformToVariableNode(
  textNode: TextNode,
  isValid: (key: string) => boolean
): boolean {
  const match = textNode.isSimpleText()
    ? matchMustacheVariableInText(textNode.getTextContent(), true)
    : null;
  if (!match) {
    return false;
  }
  const toReplace = splitNodeContainingQuery(textNode, match);
  if (!toReplace) {
    return false;
  }
  toReplace.replace(
    createVariableNode(match.matchingString, isValid(match.matchingString))
  );
  return true;
}

/**
 * Recursively walks a node tree, converting matching TextNodes to VariableNodes.
 * Repeats until stable so multiple placeholders in one paragraph are all converted
 * (each replacement can split a TextNode into siblings that were not in the initial walk).
 */
export function deepTransformToVariableNodes(
  node: LexicalNode,
  isValid: (key: string) => boolean
) {
  const runPass = (): boolean => {
    let passChanged = false;
    const walk = (n: LexicalNode) => {
      if ($isTextNode(n)) {
        if (maybeTransformToVariableNode(n, isValid)) {
          passChanged = true;
        }
      } else if ($isElementNode(n)) {
        for (const child of n.getChildren()) {
          walk(child);
        }
      }
    };
    walk(node);
    return passChanged;
  };

  while (runPass()) {
    // keep converting until no TextNode still contains a full {{...}} match
  }
}
